import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { ScrollText, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ENTITY_TYPES = ['all', 'auth', 'transaction', 'budget', 'recurring', 'receipt', 'settings', 'user'];

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { entity_type: filter, limit: 200 } : { limit: 200 };
      const res = await axios.get(`${API}/audit-logs`, { params });
      setLogs(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="p-6 md:p-12" data-testid="audit-logs-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="audit-logs-title">Audit Log</h1>
            <p className="text-slate-600 mt-1 font-ibm">Mutating actions, 200 most recent</p>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-600" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="audit-filter-select" className="w-44 bg-white border-slate-200 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((e) => <SelectItem key={e} value={e}>{e === 'all' ? 'All entities' : e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="audit-logs-loading">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="no-audit-logs">
            <ScrollText className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-xl font-chivo font-bold text-slate-900 mb-2">No audit log entries</h3>
            <p className="text-slate-600 font-ibm text-sm">Entries will appear here as users perform mutating actions.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="audit-logs-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Time</th>
                    <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">User</th>
                    <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Action</th>
                    <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Entity</th>
                    <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, idx) => (
                    <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${idx}`}>
                      <td className="px-4 py-3 font-ibm text-xs text-slate-600 whitespace-nowrap">
                        {new Date(l.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-ibm text-sm text-slate-900">{l.user_email}</td>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-800 rounded-sm">{l.action}</code>
                      </td>
                      <td className="px-4 py-3 font-ibm text-sm text-slate-700 capitalize">{l.entity_type}</td>
                      <td className="px-4 py-3 font-ibm text-xs text-slate-600 max-w-md truncate" title={JSON.stringify(l.details)}>
                        {l.details ? JSON.stringify(l.details) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AuditLogs;
